import React, { createContext, useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { STAFF_PROFILE_SELECT_FIELDS } from '../auth/profileFields';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          setProfileLoading(true);
        } else {
          setUser(null);
          setProfile(null);
          setProfileLoading(false);
        }
      } catch (err) {
        // LIVE-001: an unexpected failure here must not leave `loading` stuck
        // forever (it would block the public root route indefinitely) or
        // change existing session state. Fail safe as "not authenticated".
        console.error('Failed to load auth session:', err);
        setUser(null);
        setProfile(null);
        setProfileLoading(false);
      } finally {
        setAuthLoading(false);
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        setProfile(null);
        setProfileLoading(true);
      } else {
        setUser(null);
        setProfile(null);
        setProfileLoading(false);
      }
      setAuthLoading(false);
    });

    return () => listener?.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      if (!user) {
        setProfile(null);
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);

      const { data, error } = await supabase
        .from('profiles')
        .select(STAFF_PROFILE_SELECT_FIELDS)
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error('Failed to fetch profile:', error);
        setProfile(null);
      } else {
        setProfile(data ?? null);
      }

      setProfileLoading(false);
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const loading = authLoading || profileLoading;

  return (
    <AuthContext.Provider value={{ user, profile, loading, authLoading, profileLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
